import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../api';
import {
  ApiError,
  type MercadoCatalogo,
  type MercadoCatalogoLinha,
  type MercadoItem,
  type MercadoMeus,
  type MercadoOferta,
  type MercadoOfertaLinha,
  type Pack,
  type PackItem
} from '../api/types';
import { IconeItem } from './IconeItem';
import { PILE_PRATA } from '../assets/items';
import { numero, t } from '../i18n/idioma';
import { nomeDoGrupoDeItem, nomeDoItem, nomeDoMaterial } from '../i18n/nomes';
import { MATERIAIS, materiaisDa } from '../look/materiais';
import { IcAr, IcDano, IcEtiqueta, IcLupa, IcPergaminho, IcPeso, IcRelogio } from './icones/IconesV3';
import { comissaoDe, HORAS, percentualDe, taxaDeAnuncio } from './mercadoFmt';

/**
 * Leilão (techspec-hud-v3 §11): o Mercado com outro nome. Comprar numa vitrine com categorias; o item
 * com as ofertas em linhas, Comprar e Lance ali mesmo; Vender passo a passo, com preço fixo por padrão e
 * "vender por leilão" opcional; Meus negócios com o que está à venda, os lances e o que ficou preso.
 * As rotas continuam `mercado`; tudo é validado no servidor (saldo, dono, limite, fora de caçada).
 */
const GRUPOS = ['weapon', 'armor', 'shield', 'cloth', 'mount', 'ammo', 'ore', 'ingot', 'potion', 'misc'] as const;
const COR_MATERIAL = new Map(MATERIAIS.map(m => [m.id, m.cor]));

type Aba = 'comprar' | 'vender' | 'negocios';
/** `pix` é só a vitrine do que vem (techspec-pix-leilao): a aba existe, o conteúdo diz "em breve". */
type Neg = 'venda' | 'lances' | 'presos' | 'pix';

interface Props {
  active: boolean;
  tick: number;
  onChanged(text: string): void;
}

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function Pr({ n }: { n: number }) {
  return <span className="lei-p"><img className="lei-prata" src={PILE_PRATA} alt="" />{numero(Math.round(n))}</span>;
}

function tempoLei(iso: string): { txt: string; cls: string } {
  const s = Math.max(0, Math.floor((Date.parse(iso) - Date.now()) / 1000));
  const cls = s < 1800 ? ' is-urg' : s < 7200 ? ' is-logo' : '';
  if (s < 3600) return { txt: t('lei.min', { n: Math.max(1, Math.floor(s / 60)) }), cls };
  if (s < 86400) return { txt: `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`, cls };
  return { txt: t('lei.diasHoras', { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600) }), cls };
}

/** Preço fixo: compra já igual ao lance mínimo e nenhum lance. */
const ehFixo = (o: { compraJa: number | null | undefined; lanceMinimo?: number; lances: number }) =>
  o.lances === 0 && o.compraJa != null && o.lanceMinimo != null && o.compraJa === o.lanceMinimo;

export function LeilaoGump({ active, tick, onChanged }: Props) {
  const [aba, setAba] = useState<Aba>('comprar');
  const [item, setItem] = useState<string | null>(null);
  const [grupo, setGrupo] = useState<string | null>(null);
  const [neg, setNeg] = useState<Neg>('venda');
  const [aberta, setAberta] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [local, setLocal] = useState(0);
  const [meus, setMeus] = useState<MercadoMeus | null>(null);
  const [vendaPronta, setVendaPronta] = useState<string | null>(null);
  const bust = tick + local;
  const toastT = useRef(0);

  useEffect(() => {
    if (!active) return;
    let vivo = true;
    void api.mercadoMeus().then(m => { if (vivo) setMeus(m); }).catch(() => {});
    return () => { vivo = false; };
  }, [active, bust]);

  const avisar = useCallback((txt: string) => {
    setToast(txt);
    window.clearTimeout(toastT.current);
    toastT.current = window.setTimeout(() => setToast(''), 2400);
  }, []);
  useEffect(() => () => window.clearTimeout(toastT.current), []);

  const mudou = (txt: string) => {
    setLocal(n => n + 1);
    avisar(txt);
    onChanged(txt);
  };

  if (!active) return null;

  const pip = (meus?.retirar.length ?? 0) + (meus?.lances.filter(l => !l.vencendo).length ?? 0);
  const saldo = meus?.saldo ?? { cofre: 0, mochila: 0 };
  const livre = saldo.cofre + saldo.mochila;
  const abas: [Aba, string, ReactNode, number][] = [
    ['comprar', t('lei.comprar'), <IcLupa key="l" />, 0],
    ['vender', t('lei.vender'), <IcEtiqueta key="e" />, 0],
    ['negocios', t('lei.negocios'), <IcPergaminho key="p" />, pip]
  ];
  const irPara = (lotId: string, defId: string, abrir: boolean) => {
    setAba('comprar');
    setItem(defId);
    setAberta(abrir ? lotId : null);
  };

  return (
    <>
      <div className="lei-topo">
        <nav className="lei-abas" role="tablist" aria-label={t('hud.menu.leilao')}>
          {abas.map(([id, nome, ic, n]) => (
            <button key={id} type="button" role="tab" aria-selected={aba === id} aria-pressed={aba === id}
              onClick={() => { setAba(id); setItem(null); setAberta(null); }}>
              {ic}{nome}{n ? <i>{n}</i> : null}
            </button>
          ))}
        </nav>
        <span className="lei-saldo" title={t('lei.saldoDica', { cofre: numero(saldo.cofre), mochila: numero(saldo.mochila) })}>
          <img className="lei-prata" src={PILE_PRATA} alt="" />{numero(livre)}<small>{t('lei.prata')}</small>
        </span>
      </div>
      <div className="lei-corpo">
        {aba === 'comprar' && !item && <Comprar tick={bust} grupo={grupo} onGrupo={setGrupo} onItem={id => { setItem(id); setAberta(null); }} />}
        {aba === 'comprar' && item && (
          <Item
            defId={item}
            tick={bust}
            livre={livre}
            grupo={grupo}
            aberta={aberta}
            onAberta={setAberta}
            onVolta={() => { setItem(null); setAberta(null); }}
            onVender={() => { setAba('vender'); setItem(null); }}
            onMudou={mudou}
            onErro={avisar}
          />
        )}
        {aba === 'vender' && (
          <Vender
            tick={bust}
            meus={meus}
            preSel={vendaPronta}
            onMudou={txt => { setVendaPronta(null); mudou(txt); setAba('negocios'); setNeg('venda'); }}
            onErro={avisar}
          />
        )}
        {aba === 'negocios' && (
          <Negocios
            meus={meus}
            neg={neg}
            onNeg={setNeg}
            onVer={(lotId, defId) => irPara(lotId, defId, false)}
            onCobrir={(lotId, defId) => irPara(lotId, defId, true)}
            onVender={() => setAba('vender')}
            onMudou={mudou}
            onErro={avisar}
          />
        )}
      </div>
      {toast && <div className="lei-toast" role="status">{toast}</div>}
    </>
  );
}

/* ------------------------------------------------------------------ comprar: categorias e vitrine */
function Comprar({ tick, grupo, onGrupo, onItem }: { tick: number; grupo: string | null; onGrupo(g: string | null): void; onItem(id: string): void }) {
  const [cat, setCat] = useState<MercadoCatalogo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [soOferta, setSoOferta] = useState(true);
  const [ordem, setOrdem] = useState<'ofertas' | 'barato' | 'nome'>('ofertas');

  useEffect(() => {
    let vivo = true;
    void api.mercadoCatalogo({})
      .then(c => { if (vivo) { setCat(c); setErro(null); } })
      .catch(e => { if (vivo) setErro(e instanceof ApiError ? e.message : t('mercado.naoAbriu')); });
    return () => { vivo = false; };
  }, [tick]);

  const todos = cat?.itens ?? [];
  const nome = (c: MercadoCatalogoLinha) => nomeDoItem(c.defId, c.nome);
  const busca = semAcento(q.trim());
  const lista = useMemo(() => {
    const l = todos.filter(c => (!grupo || c.grupo === grupo) && (!busca || semAcento(nome(c)).includes(busca)) && (!soOferta || c.ofertas > 0));
    return l.sort(ordem === 'barato' ? (a, b) => (a.menorPreco ?? 1e12) - (b.menorPreco ?? 1e12)
      : ordem === 'nome' ? (a, b) => nome(a).localeCompare(nome(b))
        : (a, b) => b.ofertas - a.ofertas || nome(a).localeCompare(nome(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos, grupo, busca, soOferta, ordem]);
  const amostra = (g: string) => (todos.find(c => c.grupo === g && c.ofertas) ?? todos.find(c => c.grupo === g))?.defId;
  const conta = (g: string | null) => todos.filter(c => (!g || c.grupo === g) && c.ofertas).length;

  return (
    <div className="lei-comprar">
      <nav className="lei-cats" aria-label={t('mercado.categoria')}>
        {[null, ...GRUPOS].map(g => {
          const a = g ? amostra(g) : null;
          const n = conta(g);
          return (
            <button key={g ?? 'tudo'} type="button" className="lei-cat" aria-pressed={grupo === g} onClick={() => onGrupo(g)}>
              <span className="lei-cat__ic">{g ? (a ? <IconeItem id={a} /> : null) : <IcLupa />}</span>
              <span>{g ? nomeDoGrupoDeItem(g) : t('mercado.tudo')}</span>
              <small>{n || ''}</small>
            </button>
          );
        })}
      </nav>
      <div className="lei-vit">
        <div className="lei-busca">
          <label className="lei-q"><IcLupa /><input value={q} onChange={e => setQ(e.target.value)} maxLength={40} placeholder={t('lei.procura')} aria-label={t('lei.buscarAria')} autoComplete="off" /></label>
          <button type="button" className="lei-tg" aria-pressed={soOferta} onClick={() => setSoOferta(v => !v)}><span className="cb-sw" aria-pressed={soOferta} />{t('lei.soAVenda')}</button>
          {/* Pix entre jogadores (techspec-pix-leilao): por enquanto só anuncia que vem. */}
          <span className="lei-tg is-breve" aria-disabled="true" title={t('lei.pix.soPixDica')}><span className="cb-sw" aria-pressed={false} />{t('lei.pix.soPix')}<em className="p-nova">{t('comum.emBreve')}</em></span>
          <select className="lei-sel" aria-label={t('lei.ordenar')} value={ordem} onChange={e => setOrdem(e.target.value as typeof ordem)}>
            <option value="ofertas">{t('lei.maisOfertas')}</option>
            <option value="barato">{t('lei.maisBarato')}</option>
            <option value="nome">{t('lei.az')}</option>
          </select>
        </div>
        {erro && <p className="notice" role="alert">{erro}</p>}
        <span className="lei-conta">
          {t(lista.length === 1 ? 'lei.umItem' : 'lei.nItens', { n: lista.length })}
          {soOferta ? ` ${t('lei.aVenda')}` : ''}
          {grupo ? ` ${t('lei.em', { grupo: nomeDoGrupoDeItem(grupo) })}` : ''}
        </span>
        {!cat ? <p className="lei-vazio">{t('comum.montando')}</p> : lista.length ? (
          <div className="lei-grade">
            {lista.map(c => (
              <button key={c.defId} type="button" className={`lei-card${c.ofertas ? '' : ' is-sem'}`} onClick={() => onItem(c.defId)}>
                {c.ofertas ? <span className="lei-card__n2">{c.ofertas}</span> : null}
                <span className="lei-card__ic"><IconeItem id={c.defId} /></span>
                <span className="lei-card__n">{nome(c)}</span>
                <span className="lei-card__p">
                  {c.menorPreco != null
                    ? <><small>{c.stackable ? t('lei.aPartirCada') : t('lei.aPartir')}</small><Pr n={Math.max(1, c.menorPreco)} /></>
                    : <>{t('lei.semOferta')}{c.lojaPreco != null ? ` · ${t('lei.lojaN', { n: numero(c.lojaPreco) })}` : ''}</>}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="lei-vazio">
            {soOferta ? t('lei.nadaAVenda') : t('lei.nada')}
            {soOferta && <><br /><button type="button" className="btn btn--tiny" onClick={() => setSoOferta(false)}>{t('lei.verTudo')}</button></>}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ o item: referência, filtros, ofertas */
function Item({ defId, tick, livre, grupo, aberta, onAberta, onVolta, onVender, onMudou, onErro }: {
  defId: string; tick: number; livre: number; grupo: string | null; aberta: string | null;
  onAberta(id: string | null): void; onVolta(): void; onVender(): void; onMudou(txt: string): void; onErro(txt: string): void;
}) {
  const [it, setIt] = useState<MercadoItem | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mat, setMat] = useState<string | null>(null);
  const [soJa, setSoJa] = useState(false);
  const [soPago, setSoPago] = useState(false);
  const [ordem, setOrdem] = useState<'barato' | 'termina' | 'caro'>('barato');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setMat(null); setSoJa(false); setSoPago(false); }, [defId]);
  useEffect(() => {
    let vivo = true;
    void api.mercadoItem(defId, { material: mat ?? undefined, soImediata: soJa, soPago, ordem })
      .then(x => { if (vivo) { setIt(x); setErro(null); } })
      .catch(e => { if (vivo) setErro(e instanceof ApiError ? e.message : t('mercado.naoAbriu')); });
    return () => { vivo = false; };
  }, [defId, mat, soJa, soPago, ordem, tick]);

  const agir = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      onAberta(null);
      onMudou(ok);
    } catch (e) {
      onErro(e instanceof ApiError ? e.message : t('mercado.naoAbriu'));
    } finally {
      setBusy(false);
    }
  };

  if (!it) return erro ? <p className="notice" role="alert">{erro}</p> : <p className="lei-vazio">{t('comum.montando')}</p>;
  const nome = nomeDoItem(it.defId, it.nome);
  const nomeLote = (o: MercadoOfertaLinha) => `${o.material ? `${nomeDoMaterial(o.material)} · ` : ''}${nome}`;

  return (
    <div className="lei-item">
      <button type="button" className="lei-volta" onClick={onVolta}>← {t('lei.comprar')}{grupo ? ` · ${nomeDoGrupoDeItem(grupo)}` : ''}</button>
      <div className="lei-ih">
        <span className="lei-ih__ic"><IconeItem id={it.defId} /></span>
        <div>
          <h3>{nome}</h3>
          <div className="lei-chips">
            {it.dano && <span className="lei-chip"><IcDano />{it.dano}</span>}
            {it.ar ? <span className="lei-chip"><IcAr />AR {it.ar}</span> : null}
            <span className="lei-chip"><IcPeso />{String(it.peso).replace('.', ',')} st</span>
            {it.stackable && <span className="lei-chip">{t('lei.empilha')}</span>}
          </div>
        </div>
        <div className="lei-ref">
          <h5>{t('lei.referencia')}</h5>
          {it.lojaPreco == null && it.mediana == null
            ? <p>{t('lei.semReferencia')}</p>
            : <>
              {it.lojaPreco != null && <div><span>{t('lei.lojaNpc')}</span><Pr n={it.lojaPreco} /></div>}
              {it.mediana != null && <div><span>{t('lei.media', { n: it.vendidas })}</span><Pr n={it.mediana} /></div>}
            </>}
        </div>
      </div>
      <div className="lei-filtros">
        {it.familia && (
          <>
            <button type="button" className="lei-f" aria-pressed={!mat} onClick={() => setMat(null)}>
              {t(it.familia === 'madeira' ? 'mercado.qualquerMadeira'
                : it.familia === 'couro' ? 'mercado.qualquerCouro'
                  : it.familia === 'osso' ? 'mercado.qualquerOsso' : 'mercado.qualquerMetal')}
            </button>
            {materiaisDa(it.familia).filter(m => !m.padrao).map(m => (
              <button key={m.id} type="button" className={`lei-f${it.materiais.includes(m.id) ? '' : ' is-vazio'}`} aria-pressed={mat === m.id} onClick={() => setMat(m.id)}>
                <span className="lei-dot" style={{ background: m.cor }} />{nomeDoMaterial(m.id)}
              </button>
            ))}
          </>
        )}
        <button type="button" className="lei-f" aria-pressed={soJa} onClick={() => setSoJa(v => !v)}>{t('lei.compraNaHora')}</button>
        <button type="button" className="lei-f" aria-pressed={soPago} onClick={() => setSoPago(v => !v)}>{t('lei.cabeNoSaldo')}</button>
        <select className="lei-sel" aria-label={t('lei.ordenarOfertas')} value={ordem} onChange={e => setOrdem(e.target.value as typeof ordem)}>
          <option value="barato">{t('lei.maisBarato')}</option>
          <option value="termina">{t('lei.terminaAntes')}</option>
          <option value="caro">{t('lei.maisCaro')}</option>
        </select>
      </div>
      {erro && <p className="notice" role="alert">{erro}</p>}
      {it.ofertas.length ? (
        <div className="lei-ofs">
          {it.ofertas.map(o => {
            const fixo = ehFixo(o);
            const ab = aberta === o.id;
            const caro = (o.compraJa ?? 0) > livre;
            const tm = tempoLei(o.terminaEm);
            return (
              <div key={o.id} className="lei-of-w">
                <div className={`lei-of${o.seu ? ' is-seu' : ''}${ab ? ' is-aberta' : ''}`}>
                  <span className="lei-of__ic">
                    <IconeItem id={o.defId} />
                    {o.material && <span className="lei-dot" style={{ background: COR_MATERIAL.get(o.material) }} title={nomeDoMaterial(o.material)} />}
                  </span>
                  <span>
                    <span className="lei-of__n">
                      <b>{o.material ? `${nomeDoMaterial(o.material)} · ` : ''}{o.qty > 1 ? `${o.qty}×` : ''}</b>{o.vendedor}{o.seu && <em>{t('lei.seuAnuncio')}</em>}
                    </span>
                    <span className="lei-of__m">
                      <span className={`lei-t${tm.cls}`} title={t('lei.terminaEm', { t: tm.txt })}><IcRelogio />{tm.txt}</span>
                      {o.dur != null && o.durMax != null && (
                        <span className="lei-dur" title={t('lei.durabilidade', { d: o.dur, max: o.durMax })}><i><b style={{ width: `${(o.dur / o.durMax) * 100}%` }} /></i>{o.dur}/{o.durMax}</span>
                      )}
                      <span>{fixo ? <span className="lei-tipo">{t('lei.precoFixo')}</span> : <><span className="lei-tipo lei-tipo--lei">{t('lei.leilao')}</span> {o.lances ? t(o.lances === 1 ? 'lei.umLance' : 'lei.nLances', { n: o.lances }) : t('lei.semLance')}</>}</span>
                      {o.qty > 1 && <span>{t('lei.cada', { n: numero(Math.round(o.preco / o.qty)) })}</span>}
                    </span>
                  </span>
                  <span className="lei-of__p"><Pr n={o.preco} /><small>{fixo ? t('lei.preco') : o.lances ? t('lei.lanceAtual') : t('lei.lanceInicial')}</small></span>
                  <span className="lei-of__a">
                    {o.seu ? (
                      <>
                        {!fixo && <button type="button" className="btn btn--tiny btn--ghost" onClick={() => onAberta(ab ? null : o.id)}>{ab ? t('lei.fechar') : t('lei.verLances')}</button>}
                        <button type="button" className="btn btn--tiny" disabled={busy || o.lances > 0} title={o.lances ? t('lei.haLance') : undefined}
                          onClick={() => void agir(() => api.mercadoCancelar(o.id), t('lei.cancelou'))}>{t('lei.cancelar')}</button>
                      </>
                    ) : (
                      <>
                        {o.compraJa != null && (
                          <button type="button" className="btn btn--tiny btn--primary" disabled={busy || caro} title={caro ? t('lei.saldoNaoCobre') : undefined}
                            onClick={() => void agir(() => api.mercadoComprarJa(o.id), t('lei.comprou', { item: nomeLote(o) }))}>
                            {t('lei.comprarPor')} <Pr n={o.compraJa} />
                          </button>
                        )}
                        {!fixo && <button type="button" className="btn btn--tiny" aria-expanded={ab} onClick={() => onAberta(ab ? null : o.id)}>{ab ? t('lei.fechar') : t('lei.lance')}</button>}
                      </>
                    )}
                  </span>
                </div>
                {ab && <PainelLance lotId={o.id} livre={livre} seu={o.seu} tick={tick} onLance={teto => agir(() => api.mercadoLance(o.id, teto), t('lei.lanceDado', { n: numero(teto) }))} />}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="lei-vazio">
          {soJa || soPago || mat ? t('lei.nenhumaComFiltros') : t('lei.ninguemVende', { item: nome })}
          <br /><button type="button" className="btn btn--tiny" onClick={onVender}>{t('lei.venderOMeu')}</button>
        </p>
      )}
    </div>
  );
}

function PainelLance({ lotId, livre, seu, tick, onLance }: { lotId: string; livre: number; seu: boolean; tick: number; onLance(teto: number): Promise<void> }) {
  const [of, setOf] = useState<MercadoOferta | null>(null);
  const [teto, setTeto] = useState('');
  useEffect(() => {
    let vivo = true;
    void api.mercadoOferta(lotId).then(o => { if (vivo) { setOf(o); setTeto(String(o.proximoLance)); } }).catch(() => {});
    return () => { vivo = false; };
  }, [lotId, tick]);
  if (!of) return <div className="lei-lance"><p className="lei-dica">{t('comum.montando')}</p></div>;
  const valor = Number(teto) || of.proximoLance;
  const passo = Math.max(1, Math.round(of.proximoLance * 0.05));
  const hist = of.historico.length
    ? of.historico.map((h, i) => <div key={`${h.em}-${i}`}><span>{i === 0 ? '👑 ' : ''}{h.nome}</span><Pr n={h.teto} /></div>)
    : <p>{t('lei.semLancesAinda')}</p>;
  if (seu) {
    return (
      <div className="lei-lance">
        <div className="lei-hist"><h5>{t('lei.lances')}</h5>{hist}</div>
        <div className="lei-teto"><p className="lei-dica">{of.historico.length ? t('lei.seuComLance') : t('lei.seuSemLance')}</p></div>
      </div>
    );
  }
  return (
    <div className="lei-lance">
      <div className="lei-hist"><h5>{t('lei.lances')}</h5>{hist}</div>
      <div className="lei-teto">
        <h5 className="lei-passo">{t('lei.seuTeto')}</h5>
        <div className="lei-num">
          <button type="button" aria-label={t('cb.menos')} onClick={() => setTeto(String(Math.max(of.proximoLance, valor - passo)))}>−</button>
          <label><img className="lei-prata" src={PILE_PRATA} alt="" /><input inputMode="numeric" value={teto} aria-label={t('lei.seuTeto')} onChange={e => setTeto(e.target.value.replace(/\D/g, ''))} /></label>
          <button type="button" aria-label={t('cb.mais')} onClick={() => setTeto(String(valor + passo))}>+</button>
        </div>
        <div className="lei-rap">
          <button type="button" className="lei-f" onClick={() => setTeto(String(of.proximoLance))}>{t('lei.minimo', { n: numero(of.proximoLance) })}</button>
          <button type="button" className="lei-f" onClick={() => setTeto(String(Math.ceil(valor * 1.1)))}>+10%</button>
          {of.compraJa != null && <button type="button" className="lei-f" onClick={() => setTeto(String(of.compraJa))}>{t('lei.ateCompraJa')}</button>}
        </div>
        <p className="lei-dica">{t('lei.tetoDica')} <Pr n={livre - valor} /></p>
        <button type="button" className="btn btn--primary" disabled={valor < of.proximoLance || valor > livre} onClick={() => void onLance(valor)}>
          {t('lei.darLance', { n: numero(valor) })}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ vender */
function Vender({ tick, meus, preSel, onMudou, onErro }: { tick: number; meus: MercadoMeus | null; preSel: string | null; onMudou(txt: string): void; onErro(txt: string): void }) {
  const [pack, setPack] = useState<Pack | null>(null);
  const [sel, setSel] = useState<string | null>(preSel);
  const [minimo, setMinimo] = useState('');
  const [ja, setJa] = useState('');
  const [leilao, setLeilao] = useState(false);
  const [qty, setQty] = useState(1);
  const [horas, setHoras] = useState<number>(24);
  const [ref, setRef] = useState<MercadoItem | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let vivo = true;
    void api.getPack().then(p => { if (vivo) setPack(p); }).catch(() => {});
    return () => { vivo = false; };
  }, [tick]);

  const item = pack?.items.find(i => i.id === sel) ?? null;
  useEffect(() => {
    if (!item) { setRef(null); return; }
    let vivo = true;
    void api.mercadoItem(item.defId, {}).then(r => { if (vivo) setRef(r); }).catch(() => { if (vivo) setRef(null); });
    return () => { vivo = false; };
  }, [item?.defId]);

  // Abençoado também vai ao Leilão (Renato, 28/10/2026): a marca segue com a peça.
  const motivo = (m: PackItem) => m.equippedSlot ? 'vestido' : m.defId === 'silver' || m.defId === 'gold' ? 'moeda' : '';
  const escolher = (m: PackItem) => { if (motivo(m)) return; setSel(m.id); setQty(m.qty); setMinimo(''); setJa(''); setLeilao(false); };

  const min = Number(minimo) || 0;
  const cja = !leilao ? min : ja === '' ? null : Number(ja);
  const taxa = min ? taxaDeAnuncio(min, cja, horas) : 0;
  const comissao = comissaoDe(min);
  const recebe = Math.max(0, min - comissao);
  const cheio = !!meus && meus.anunciosAtivos >= meus.anunciosTeto;
  const empilha = !!item && item.qty > 1;
  const q = empilha ? qty : 1;
  const menor = ref?.ofertas.filter(o => !o.seu).map(o => o.preco / Math.max(1, o.qty));
  const sug: [string, number][] = [];
  if (menor?.length) sug.push([t('lei.menorAVenda'), Math.round(Math.min(...menor) * q)]);
  if (ref?.mediana != null) sug.push([t('lei.mediaVendas'), Math.round(ref.mediana * q)]);
  if (ref?.lojaPreco != null) sug.push([t('lei.lojaNpc'), Math.round(ref.lojaPreco * q)]);

  const anunciar = async () => {
    if (!item || !min || cheio) return;
    setBusy(true);
    try {
      await api.mercadoAnunciar({ itemId: item.id, qty: empilha ? q : undefined, lanceMinimo: min, compraJa: cja != null ? Math.max(cja, min) : null, horas });
      setSel(null);
      const nome = nomeDoItem(item.defId, item.name);
      onMudou(leilao ? t('lei.leilaoAberto', { item: nome, n: numero(min) }) : t('lei.aVendaOk', { item: nome, n: numero(min) }));
    } catch (e) {
      onErro(e instanceof ApiError ? e.message : t('mercado.naoAnunciou'));
    } finally {
      setBusy(false);
    }
  };

  const campoPrata = (valor: string, set: (v: string) => void, rotulo: string, placeholder = '0') => (
    <span className="lei-num" style={{ gridTemplateColumns: '1fr' }}>
      <label><img className="lei-prata" src={PILE_PRATA} alt="" /><input inputMode="numeric" value={valor} placeholder={placeholder} aria-label={rotulo} onChange={e => set(e.target.value.replace(/\D/g, ''))} /></label>
    </span>
  );

  return (
    <div className="lei-vender">
      <div>
        <h5 className="lei-passo">{t('lei.suaMochila')}</h5>
        <div className="lei-moch">
          {pack?.items.map(m => {
            const off = motivo(m);
            const nome = nomeDoItem(m.defId, m.name);
            return (
              <button key={m.id} type="button" className={`lei-mi${off ? ' is-off' : ''}`} aria-pressed={sel === m.id} aria-disabled={off ? true : undefined}
                title={off ? t(`lei.motivo.${off}Dica`, { item: nome }) : nome} aria-label={off ? t(`lei.motivo.${off}Dica`, { item: nome }) : nome}
                onClick={() => escolher(m)}>
                <IconeItem id={m.defId} />
                {off ? <em>{t(`lei.motivo.${off}`)}</em> : m.qty > 1 ? <b>{numero(m.qty)}</b> : null}
              </button>
            );
          })}
        </div>
      </div>
      <div className="lei-form">
        {!item ? <div className="lei-nada"><IcEtiqueta />{t('lei.escolhaNaMochila')}</div> : (
          <>
            <div className="lei-ih">
              <span className="lei-ih__ic"><IconeItem id={item.defId} /></span>
              <div>
                <h3>{nomeDoItem(item.defId, item.name)}</h3>
                <div className="lei-chips">
                  <span className="lei-chip">{t('lei.naMochila', { n: numero(item.qty) })}</span>
                  {item.dur != null && item.durMax != null && <span className="lei-chip">dur {item.dur}/{item.durMax}</span>}
                </div>
              </div>
            </div>
            {empilha && (
              <div className="lei-campo">
                <h5 className="lei-passo">{t('lei.quantos')}</h5>
                <div className="lei-num" style={{ maxWidth: 200 }}>
                  <button type="button" aria-label={t('cb.menos')} onClick={() => setQty(n => Math.max(1, n - (item.qty >= 20 ? 5 : 1)))}>−</button>
                  <label><input inputMode="numeric" value={qty} aria-label={t('mercado.quantidade')} onChange={e => setQty(Math.max(1, Math.min(item.qty, Number(e.target.value.replace(/\D/g, '')) || 1)))} /></label>
                  <button type="button" aria-label={t('cb.mais')} onClick={() => setQty(n => Math.min(item.qty, n + (item.qty >= 20 ? 5 : 1)))}>+</button>
                </div>
              </div>
            )}
            <div className="lei-campo">
              <h5 className="lei-passo">{q > 1 ? t('lei.porQuantoLote', { n: q }) : t('lei.porQuanto')}</h5>
              {leilao ? (
                <>
                  <div className="lei-campos">
                    <label className="lei-campo"><span>{t('lei.lanceInicial')}</span>{campoPrata(minimo, setMinimo, t('lei.lanceInicial'))}</label>
                    <label className="lei-campo"><span>{t('lei.precoCompraOpc')}</span>{campoPrata(ja, setJa, t('lei.precoCompra'), t('lei.emAberto'))}</label>
                  </div>
                  <p className="lei-dica">{t('lei.leilaoDica')}</p>
                </>
              ) : (
                <label className="lei-campo"><span>{t('lei.precoFixoVenda')}</span>{campoPrata(minimo, setMinimo, t('lei.precoFixoVenda'))}</label>
              )}
              {sug.length
                ? <div className="lei-rap">{sug.map(([rot, n]) => <button key={rot} type="button" className="lei-f" onClick={() => setMinimo(String(n))}>{rot} {numero(n)}</button>)}</div>
                : <p className="lei-dica">{t('lei.semRefVenda')}</p>}
              <button type="button" className="lei-tg" aria-pressed={leilao} onClick={() => setLeilao(v => !v)}><span className="cb-sw" aria-pressed={leilao} />{t('lei.venderPorLeilao')}</button>
            </div>
            <div className="lei-campo">
              <h5 className="lei-passo">{t('lei.porQuantoTempo')}</h5>
              <div className="lei-seg">
                {HORAS.map(h => <button key={h} type="button" aria-pressed={horas === h} onClick={() => setHoras(h)}>{h} h<small>{t('lei.taxaP', { p: percentualDe(h) })}</small></button>)}
              </div>
            </div>
            <PixEmBreve />
            <div className="lei-recibo">
              <div><span>{t('lei.taxaAnunciar')}</span><Pr n={taxa} /></div>
              <div><span>{t('lei.comissao', { p: 4 })}</span><Pr n={comissao} /></div>
              <div className="is-total"><span>{leilao ? t('lei.recebeMinimo') : t('lei.recebe')}</span><Pr n={recebe} /></div>
            </div>
            <p className="lei-dica">{t('lei.saiDaMochila', { n: meus?.anunciosAtivos ?? 0, max: meus?.anunciosTeto ?? 10 })}</p>
            <button type="button" className="btn btn--primary" disabled={busy || !min || cheio} onClick={() => void anunciar()}>
              {cheio ? t('lei.limite') : !min ? (leilao ? t('lei.digaLance') : t('lei.digaPreco')) : t(leilao ? 'lei.abrirLeilao' : 'lei.porAVenda', { n: numero(taxa) })}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ meus negócios */
function Negocios({ meus, neg, onNeg, onVer, onCobrir, onVender, onMudou, onErro }: {
  meus: MercadoMeus | null; neg: Neg; onNeg(n: Neg): void; onVer(lotId: string, defId: string): void; onCobrir(lotId: string, defId: string): void;
  onVender(): void; onMudou(txt: string): void; onErro(txt: string): void;
}) {
  const [busy, setBusy] = useState(false);
  const agir = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); onMudou(ok); } catch (e) { onErro(e instanceof ApiError ? e.message : t('mercado.naoAbriu')); } finally { setBusy(false); }
  };
  if (!meus) return <p className="lei-vazio">{t('comum.montando')}</p>;
  const nome = (defId: string, n: string, mat?: string | null) => `${mat ? `${nomeDoMaterial(mat)} · ` : ''}${nomeDoItem(defId, n)}`;
  const ic = (defId: string) => <span className="lei-of__ic"><IconeItem id={defId} /></span>;
  const subs: [Neg, string][] = [
    ['venda', t('lei.subVenda', { n: meus.anunciosAtivos, max: meus.anunciosTeto })],
    ['lances', t('lei.subLances', { n: meus.lances.length })],
    ['presos', t('lei.subPresos', { n: meus.retirar.length })],
    ['pix', t('lei.pix.sub')]
  ];
  return (
    <div className="lei-neg">
      <p className="lei-aviso">{t('lei.avisoBanco')}</p>
      <div className="lei-sub">{subs.map(([id, rot]) => <button key={id} type="button" className="lei-f" aria-pressed={neg === id} onClick={() => onNeg(id)}>{rot}{id === 'pix' && <em className="p-nova">{t('comum.emBreve')}</em>}</button>)}</div>
      <div className="lei-ofs">
        {neg === 'venda' && (meus.anuncios.length ? meus.anuncios.map(a => {
          const tm = tempoLei(a.terminaEm);
          const fixo = ehFixo({ compraJa: a.compraJa, lanceMinimo: a.lanceMinimo, lances: a.lances });
          return (
            <div key={a.id} className="lei-nc">
              {ic(a.defId)}
              <span>
                <span className="lei-of__n"><b>{nome(a.defId, a.nome, a.material)}{a.qty > 1 ? ` ×${a.qty}` : ''}</b></span>
                <span className="lei-of__m">
                  <span className={`lei-t${tm.cls}`}><IcRelogio />{tm.txt}</span>
                  <span>{fixo ? <span className="lei-tipo">{t('lei.precoFixo')}</span> : <><span className="lei-tipo lei-tipo--lei">{t('lei.leilao')}</span> {a.lances ? t(a.lances === 1 ? 'lei.umLance' : 'lei.nLances', { n: a.lances }) : t('lei.semLanceAinda')}</>}</span>
                </span>
              </span>
              <span className="lei-of__p"><Pr n={a.preco} /><small>{fixo ? t('lei.preco') : a.lances ? t('lei.lanceAtual') : t('lei.lanceInicial')}</small></span>
              <span className="lei-of__a">
                <button type="button" className="btn btn--tiny btn--ghost" onClick={() => onVer(a.id, a.defId)}>{t('lei.ver')}</button>
                <button type="button" className="btn btn--tiny" disabled={busy || a.lances > 0} title={a.lances ? t('lei.haLance') : undefined}
                  onClick={() => void agir(() => api.mercadoCancelar(a.id), t('lei.cancelou'))}>{t('lei.cancelar')}</button>
              </span>
            </div>
          );
        }) : <p className="lei-vazio">{t('lei.nadaAVendaMeu')}<br /><button type="button" className="btn btn--tiny" onClick={onVender}>{t('lei.venderAlgo')}</button></p>)}
        {neg === 'lances' && (meus.lances.length ? meus.lances.map(b => {
          const tm = tempoLei(b.terminaEm);
          return (
            <div key={b.lotId} className="lei-nc">
              {ic(b.defId)}
              <span>
                <span className="lei-of__n"><b>{nome(b.defId, b.nome, b.material)}</b></span>
                <span className="lei-of__m"><span className={`lei-t${tm.cls}`}><IcRelogio />{tm.txt}</span><span>{t('lei.seuTetoN', { n: numero(b.teto) })}</span></span>
              </span>
              <span className={`lei-st ${b.vencendo ? 'lei-st--ok' : 'lei-st--mal'}`}>{b.vencendo ? t('lei.ganhando') : t('lei.passaram')}</span>
              <span className="lei-of__a">
                {b.vencendo
                  ? <button type="button" className="btn btn--tiny btn--ghost" onClick={() => onVer(b.lotId, b.defId)}>{t('lei.ver')}</button>
                  : <button type="button" className="btn btn--tiny btn--primary" onClick={() => onCobrir(b.lotId, b.defId)}>{t('lei.cobrir')}</button>}
              </span>
            </div>
          );
        }) : <p className="lei-vazio">{t('lei.semLanceNoAr')}</p>)}
        {neg === 'pix' && (
          <div className="lei-pix-breve">
            <span className="lei-pix__selo">{t('lei.pix.selo')}</span>
            <b>{t('lei.pix.negTitulo')}</b>
            <p>{t('lei.pix.negDica')}</p>
            <em className="p-nova">{t('comum.emBreve')}</em>
          </div>
        )}
        {neg === 'presos' && (meus.retirar.length ? meus.retirar.map(r => (
          <div key={r.id} className="lei-nc">
            {ic(r.defId)}
            <span>
              <span className="lei-of__n"><b>{nome(r.defId, r.nome, r.material)}{r.qty > 1 ? ` ×${r.qty}` : ''}</b></span>
              <span className="lei-of__m"><span>{r.motivo}</span></span>
            </span>
            <span className="lei-st lei-st--neutro">{t('lei.esperandoVaga')}</span>
            <span className="lei-of__a">
              <button type="button" className="btn btn--tiny btn--primary" disabled={busy}
                onClick={() => void agir(() => api.mercadoRetirar(r.id), t('lei.naMochilaOk', { item: nomeDoItem(r.defId, r.nome) }))}>{t('lei.porNaMochila')}</button>
            </span>
          </div>
        )) : <p className="lei-vazio">{t('lei.nadaPreso')}</p>)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ pix (em breve) */
/**
 * O bloco "Aceitar Pix" do Vender, desligado: mostra ao jogador que a venda por reais vem aí
 * (techspec-pix-leilao, T2). Não manda nada ao servidor.
 */
function PixEmBreve() {
  return (
    <div className="lei-pix is-breve" aria-disabled="true">
      <div className="lei-pix__lin">
        <span className="cb-sw" aria-pressed={false} />
        <b>{t('lei.pix.aceitar')}</b>
        <span className="lei-pix__selo">{t('lei.pix.selo')}</span>
        <em className="p-nova">{t('comum.emBreve')}</em>
      </div>
      <p className="lei-dica">{t('lei.pix.vendaDica')}</p>
    </div>
  );
}
